import React, { useState, useRef } from 'react';
import {
  Badge, Button, Overlay, Popover, Spinner, Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DOMPurify from 'dompurify';

dayjs.extend(relativeTime);

/**
 * Format relative time for notification display
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  return dayjs(dateString).fromNow();
};

/**
 * Format sender display name
 * @param {string} sentBy - Email or 'system'
 * @returns {string|null} Display name
 */
const formatSender = (sentBy) => {
  if (!sentBy) return null;
  if (sentBy === 'system') return 'System';
  // Extract name from email (before @)
  const name = sentBy.split('@')[0];
  // Capitalize first letter of each word
  return name
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/**
 * NotificationBell component - Bell icon with badge and popover
 * @param {Object} props
 * @param {Array} props.notifications - List of notifications
 * @param {number} props.unreadCount - Unread notification count
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.hasMore - Whether more notifications are available
 * @param {Function} props.onFetchMore - Callback to fetch more notifications
 * @param {Function} props.onMarkAsRead - Callback to mark notifications as read
 * @param {Function} props.onMarkAllAsRead - Callback to mark all notifications as read
 * @param {Function} props.onNotificationClick - Callback when a notification is clicked
 * @param {Function} props.onRefresh - Callback to refresh notifications when popover opens
 */
function NotificationBell({
  notifications = [],
  unreadCount = 0,
  isLoading = false,
  hasMore = false,
  onFetchMore,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick,
  onRefresh,
}) {
  const [show, setShow] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const target = useRef(null);
  const navigate = useNavigate();

  const handleBellClick = () => {
    const newShow = !show;
    setShow(newShow);
    // Refresh notifications when opening the popover
    if (newShow) {
      onRefresh?.();
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.IsRead) {
      onMarkAsRead?.([notification.Id]);
    }

    // Open modal with notification content
    setSelectedNotification(notification);
    setShow(false);

    // Call custom handler
    onNotificationClick?.(notification);
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  const handleViewEntity = () => {
    if (selectedNotification?.RelatedEntityType === 'booking' && selectedNotification?.RelatedEntityId) {
      // A trip claim opens the booking where claims are reviewed.
      const tab = selectedNotification.Type === 'trip_claim' ? '&tab=driver-events' : '';
      navigate(`/orders?booking=${selectedNotification.RelatedEntityId}${tab}`);
      setSelectedNotification(null);
    }
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    onMarkAllAsRead?.();
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    onFetchMore?.();
  };

  const handleSettingsClick = (e) => {
    e.stopPropagation();
    setShow(false);
    navigate('/notification-settings');
  };

  return (
    <>
      <Button
        ref={target}
        variant="link"
        className="notification-bell-btn position-relative p-2"
        onClick={handleBellClick}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <i className="fa fa-bell text-light fs-5" />
        {unreadCount > 0 && (
          <Badge
            bg="danger"
            pill
            className="notification-badge position-absolute"
            style={{
              top: '2px',
              right: '2px',
              fontSize: '0.65rem',
              minWidth: '18px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      <Overlay
        show={show}
        target={target.current}
        placement="bottom-end"
        rootClose
        onHide={() => setShow(false)}
      >
        <Popover id="notifications-popover" className="notifications-popover">
          <Popover.Header className="d-flex justify-content-between align-items-center bg-dark text-white">
            <span>
              <i className="fa fa-bell me-2" />
              Notifications
            </span>
            {unreadCount > 0 && (
              <Button
                variant="link"
                size="sm"
                className="text-light p-0"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </Button>
            )}
          </Popover.Header>
          <Popover.Body className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 && !isLoading && (
              <div className="text-center text-muted p-4">
                <i className="fa fa-inbox fa-2x mb-2 opacity-50" />
                <p className="mb-0 small">No notifications yet</p>
              </div>
            )}

            {notifications.map((notification) => (
              <div
                key={notification.Id}
                role="button"
                tabIndex={0}
                className={`notification-item p-3 border-bottom ${!notification.IsRead ? 'notification-unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
              >
                <div className="d-flex align-items-start">
                  {!notification.IsRead && (
                    <span
                      className="notification-dot bg-primary rounded-circle me-2 flex-shrink-0"
                      style={{ width: '8px', height: '8px', marginTop: '6px' }}
                    />
                  )}
                  <div className="flex-grow-1" style={{ marginLeft: notification.IsRead ? '16px' : 0 }}>
                    <div className="fw-semibold text-truncate" style={{ maxWidth: '250px' }}>
                      {notification.Subject}
                    </div>
                    <div className="d-flex align-items-center">
                      <small className="text-muted">
                        {formatRelativeTime(notification.CreatedAt)}
                      </small>
                      {formatSender(notification.SentBy) && (
                        <small className="text-muted ms-2">
                          <i className="fa fa-user-circle me-1" style={{ fontSize: '0.7rem' }} />
                          {formatSender(notification.SentBy)}
                        </small>
                      )}
                    </div>
                  </div>
                  <i className="fa fa-chevron-right text-muted ms-2" style={{ fontSize: '0.7rem' }} />
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="text-center p-3">
                <Spinner animation="border" size="sm" variant="secondary" />
              </div>
            )}

            {hasMore && !isLoading && (
              <div className="text-center p-2 border-top">
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleLoadMore}
                >
                  Load more
                </Button>
              </div>
            )}

            {/* Settings Link */}
            <div className="text-center p-2 border-top bg-light">
              <Button
                variant="link"
                size="sm"
                className="text-muted"
                onClick={handleSettingsClick}
              >
                <i className="fa fa-cog me-1" />
                Notification Settings
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>

      {/* Notification Detail Modal */}
      <Modal
        show={!!selectedNotification}
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title className="fs-6">
            <i className="fa fa-bell me-2" />
            {selectedNotification?.Subject}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selectedNotification?.BodyType === 'html' ? (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(selectedNotification.Body, {
                  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'table', 'tr', 'td', 'th', 'thead', 'tbody'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                }),
              }}
            />
          ) : (
            <div style={{ whiteSpace: 'pre-wrap' }}>{selectedNotification?.Body}</div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div className="text-muted small">
            {selectedNotification?.CreatedAt && (
              <span>
                <i className="fa fa-clock me-1" />
                {dayjs(selectedNotification.CreatedAt).format('MMM D, YYYY h:mm A')}
              </span>
            )}
            {formatSender(selectedNotification?.SentBy) && (
              <span className="ms-3">
                <i className="fa fa-user-circle me-1" />
                {formatSender(selectedNotification?.SentBy)}
              </span>
            )}
          </div>
          <div>
            {selectedNotification?.RelatedEntityType === 'booking' && selectedNotification?.RelatedEntityId && (
              <Button variant="primary" size="sm" onClick={handleViewEntity}>
                <i className="fa fa-external-link-alt me-1" />
                View Booking
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .notification-bell-btn:hover {
            opacity: 0.8;
          }
          .notification-bell-btn:focus {
            box-shadow: none;
          }
          .notifications-popover {
            min-width: 320px;
            max-width: 350px;
          }
          /* Never exceed the viewport on small screens */
          @media (max-width: 575.98px) {
            .notifications-popover {
              min-width: calc(100vw - 1.5rem);
              max-width: calc(100vw - 1.5rem);
            }
          }
          .notification-item {
            cursor: pointer;
            transition: background-color 0.15s ease;
          }
          .notification-item:hover {
            background-color: rgba(0, 0, 0, 0.05);
          }
          .notification-unread {
            background-color: rgba(13, 110, 253, 0.05);
          }
          .notification-dot {
            animation: pulse 2s infinite;
          }
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </>
  );
}

export default NotificationBell;
